import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PropertyKeyManagement,
  PropertyMandateDto,
  PropertyMandateDuration,
  PropertyMandateType,
  PropertySellerPriority,
} from '@workspace/data-types';

interface MandateTypeDisplayInfo {
  title: string;
  badge?: string;
  badgeColorClass?: string;
  description: string;
  benefits: string[];
  drawbacks?: string[];
}

interface MandateDurationDisplayInfo {
  label: string;
  hint: string;
}

interface SellerPriorityDisplayInfo {
  icon: string;
  label: string;
  description: string;
}

const MANDATE_TYPES_MAP: Record<PropertyMandateType, MandateTypeDisplayInfo> = {
  [PropertyMandateType.SEMI_EXCLUSIVE]: {
    title: 'Mandat Semi-Exclusif',
    badgeColorClass: 'bg-primary/10 text-primary border-primary/30',
    description:
      'Un seul agent représente le vendeur pour maximiser son investissement, tout en lui conservant la liberté de trouver lui-même un acquéreur.',
    benefits: [
      'Agent très motivé et investi',
      'Honoraires négociés à la baisse',
      'Liberté de vente directe maintenue',
    ],
  },
  [PropertyMandateType.EXCLUSIVE]: {
    title: 'Mandat Exclusif',
    badgeColorClass: 'bg-amber-a3 text-amber-11 border-amber-a5',
    description:
      "L'agent bénéficie de l'exclusivité totale. En contrepartie, le vendeur bénéficie du plus haut niveau d'engagement marketing et d'un taux d'honoraires préférentiel.",
    benefits: [
      'Déploiement marketing complet (3D, drone, vitrine pro)',
      "Représente plus de 80% des ventes dans l'immobilier",
      'Un seul interlocuteur dédié',
    ],
  },
  [PropertyMandateType.SIMPLE]: {
    title: 'Mandat Simple',
    badgeColorClass: 'bg-gray-a3 border-gray-7 text-gray-11',
    description: 'Le vendeur peut mandater plusieurs agences simultanément et vendre par lui-même.',
    benefits: [
      'Liberté contractuelle totale',
      'Possibilité de vente directe par vous-même',
      'Multi-diffusion par plusieurs agences',
    ],
    drawbacks: [
      'Commission généralement plus élevée',
      'Nombreuses visites sur rendez-vous en votre présence',
      'Davantage sollicité',
    ],
  },
};

const DURATIONS_MAP: Record<PropertyMandateDuration, MandateDurationDisplayInfo> = {
  [PropertyMandateDuration.TWO_MONTHS]: {
    label: '2 mois',
    hint: 'Vente accélérée',
  },
  [PropertyMandateDuration.THREE_MONTHS]: {
    label: '3 mois',
    hint: 'Standard du marché',
  },
  [PropertyMandateDuration.SIX_MONTHS]: {
    label: '6 mois',
    hint: 'Bien spécifique / Rural',
  },
  [PropertyMandateDuration.FLEXIBLE]: {
    label: 'Flexible',
    hint: 'À convenir avec l’agent',
  },
};

const PRIORITIES_MAP: Record<PropertySellerPriority, SellerPriorityDisplayInfo> = {
  [PropertySellerPriority.QUALIFIED_BUYERS]: {
    icon: 'verified_user',
    label: 'Acquéreurs qualifiés',
    description: 'Vérification de la solvabilité bancaire avant toute visite',
  },
  [PropertySellerPriority.PREMIUM_VISUALS]: {
    icon: 'photo_camera',
    label: 'Mise en valeur premium',
    description: 'Photos professionnelles HDR, visite 3D immersive ou vidéo',
  },
  [PropertySellerPriority.DAILY_REPORTS]: {
    icon: 'chat',
    label: 'Comptes-rendus réactifs',
    description: 'Retour écrit et debrief après chaque visite sous 24-48h',
  },
  [PropertySellerPriority.SPEED]: {
    icon: 'bolt',
    label: 'Vente rapide',
    description: 'Priorité au délai de transaction et réactivité',
  },
  [PropertySellerPriority.MAX_PRICE]: {
    icon: 'payments',
    label: 'Meilleur prix',
    description: 'Défense ferme de la valeur du bien sans braderie',
  },
  [PropertySellerPriority.VALUATION]: {
    icon: 'assessment',
    label: 'Estimation du bien',
    description: 'Avis de valeur approfondi et analyse comparative du marché',
  },
  [PropertySellerPriority.FULL_DELEGATION]: {
    icon: 'key',
    label: 'Délégation totale',
    description: 'Gestion intégrale des visites et des clés en toute autonomie',
  },
};

export interface KeyManagementDisplayInfo {
  icon: string;
  label: string;
  description: string;
}

const KEY_MANAGEMENT_MAP: Record<PropertyKeyManagement, KeyManagementDisplayInfo> = {
  [PropertyKeyManagement.AGENT_KEY_DOUBLE]: {
    icon: 'key',
    label: "Double des clés remis à l'agent",
    description: 'Visites en toute autonomie avec reçu de décharge officiel',
  },
  [PropertyKeyManagement.KEY_BOX]: {
    icon: 'pin',
    label: 'Boîte à clés sécurisée à code',
    description: 'Code temporaire communiqué à chaque visite validée',
  },
  [PropertyKeyManagement.BY_APPOINTMENT_OWNER]: {
    icon: 'event_available',
    label: 'Sur rendez-vous en votre présence',
    description: "Vous conservez vos clés et assurez l'ouverture pour chaque créneau",
  },
};

@Component({
  selector: 'etxe-property-mandate-display',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './mandate-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyMandateDisplayComponent {
  mandate = input.required<PropertyMandateDto | undefined>();
  sellingPriceCents = input<number | undefined>();

  mandateTypeInfo = computed<MandateTypeDisplayInfo | null>(() => {
    const type = this.mandate()?.type;
    return type ? (MANDATE_TYPES_MAP[type] ?? null) : null;
  });

  durationInfo = computed<MandateDurationDisplayInfo | null>(() => {
    const duration = this.mandate()?.duration;
    return duration ? (DURATIONS_MAP[duration] ?? null) : null;
  });

  keyManagementList = computed<KeyManagementDisplayInfo[]>(() => {
    const km = this.mandate()?.keyManagement;
    if (!km) return [];
    const arr = Array.isArray(km) ? km : [km as unknown as PropertyKeyManagement];
    return arr.map((k) => KEY_MANAGEMENT_MAP[k]).filter((k): k is KeyManagementDisplayInfo => !!k);
  });

  prioritiesList = computed<SellerPriorityDisplayInfo[]>(() => {
    const priorities = this.mandate()?.priorities ?? [];
    return priorities.map((p) => PRIORITIES_MAP[p]).filter((p): p is SellerPriorityDisplayInfo => !!p);
  });

  sellingPriceEuros = computed(() => {
    const cents = this.sellingPriceCents();
    return cents ? cents / 100 : null;
  });

  estimatedCommissionEuros = computed(() => {
    const price = this.sellingPriceEuros();
    const rate = this.mandate()?.targetCommissionRate;
    if (!price || !rate) return null;
    return Math.round((price * rate) / 100);
  });
}
